import { MessageInstance } from 'antd/es/message/interface'
import {
  GeckoJobDataJSON,
  JobCalendarRequest,
  JobCalendarResponse,
} from 'types'

/**
 * Checks if an error message indicates that a calendar event was not found
 * @param error Error message to check
 * @returns boolean indicating if this is an "event not found" error
 */
export const isEventNotFoundError = (error: string | undefined): boolean => {
  return Boolean(error?.toLowerCase().includes('not found'))
}

/**
 * Shows a calendar-related error message to the user
 * @param message The main error message to display
 * @param error The error details (string or Error object)
 * @param messageApi Optional Antd message API instance for displaying the error
 */
export const showCalendarError = (
  message: string,
  error?: string | Error
): string => {
  const errorDetails =
    error instanceof Error ? error.message : error || 'Unknown error'
  return `${message}: ${errorDetails}`
}

/**
 * Creates a JobCalendarRequest from job data
 * @param jobData Job data to convert
 * @param jobId Job ID string
 * @param siteName Site name for the job
 * @param emailAddresses Optional list of email addresses to add as attendees
 * @returns JobCalendarRequest object
 */
export const createCalendarRequestFromJobData = (
  jobData: GeckoJobDataJSON,
  jobId: string,
  siteName?: string,
  emailAddresses?: string[]
): JobCalendarRequest => {
  const title = jobData.name || `Job at ${siteName || 'Unknown Site'}`
  const description = `Cantilever Job: ${title}\n${siteName ? `Site: ${siteName}\n` : ''}${jobData.scope?.site_address ? `Address: ${jobData.scope.site_address}\n` : ''}`

  const request: JobCalendarRequest = {
    job_id: jobId,
    title,
    description,
    start_date: new Date(jobData.start_date).toISOString(),
    end_date: new Date(jobData.end_date).toISOString(),
  }

  if (jobData.event_id) {
    request.event_id = jobData.event_id
  }

  if (jobData.scope?.site_address) {
    request.location = jobData.scope.site_address
  }

  if (emailAddresses && emailAddresses.length > 0) {
    request.email_addresses = emailAddresses
  }

  return request
}

/**
 * Checks if the job data has the minimum required fields for calendar event creation
 * @param jobData Job data to check
 * @returns boolean indicating if required fields are present
 */
export const hasRequiredCalendarFields = (
  jobData: GeckoJobDataJSON
): boolean => {
  return !!(jobData.name && jobData.start_date && jobData.end_date)
}

interface CalendarEventHandlerOptions {
  jobData: GeckoJobDataJSON
  jobId: string
  siteName: string
  messageApi: MessageInstance
  upsertCalendarEvent: (
    request: JobCalendarRequest
  ) => Promise<JobCalendarResponse>
}

/**
 * Handles calendar event creation/update operations for job data.
 * Uses .then() syntax and returns a Promise that resolves with the potentially
 * updated job data (with event_id if a new event was created).
 */
export const handleCalendarEventOperation = ({
  jobData,
  jobId,
  siteName,
  messageApi,
  upsertCalendarEvent,
}: CalendarEventHandlerOptions): Promise<GeckoJobDataJSON> => {
  // If job doesn't have required calendar fields, return job data as-is
  if (!hasRequiredCalendarFields(jobData)) {
    return Promise.resolve(jobData)
  }

  const operation = jobData.event_id ? 'Updating' : 'Creating'
  messageApi.loading(`${operation} calendar event...`, 0)

  const calendarRequest = createCalendarRequestFromJobData(
    jobData,
    jobId,
    siteName
  )

  return upsertCalendarEvent(calendarRequest)
    .then((response) => {
      messageApi.destroy()

      if (response.success) {
        // Create updated job data with event_id if it was created
        const updatedJobData = { ...jobData }
        if (response.event_id && !jobData.event_id) {
          updatedJobData.event_id = response.event_id
        }

        const successMessage = jobData.event_id
          ? 'Calendar event updated successfully'
          : 'Calendar event created successfully'
        messageApi.success(successMessage)

        return updatedJobData
      } else {
        const failureMessage = jobData.event_id
          ? 'Job saved but calendar event update failed'
          : 'Job saved but calendar event creation failed'
        messageApi.warning(failureMessage)

        return jobData
      }
    })
    .catch((error) => {
      messageApi.destroy()
      messageApi.warning(
        'Job will be saved but calendar event operation failed with error: ' +
          error
      )
      return jobData
    })
}
